import { acClient } from '@/clients/ac-client.js';
import type { User } from '@/clients/ac-client.js';
import { backboneService } from '@/services/backbone.js';
import { toastService } from '@/services/toast.js';
import { HatElement } from '@/components/hat-element.js';

class HatAdminUsers extends HatElement {
  private _users: User[] = [];
  private _nextPageToken: string | null = null;

  connectedCallback() {
    super.connectedCallback();
    this.renderShell();
    void this.loadUsers();
  }

  async loadUsers(pageToken: string | null = null) {
    try {
      const { users, nextPageToken } = await acClient.getUsers(pageToken);
      this._users = pageToken ? [...this._users, ...users] : users;
      this._nextPageToken = nextPageToken;
      this.renderContent();
    } catch {
      toastService.show('Failed to load users.');
    }
  }

  renderShell() {
    this.root.innerHTML = `
      <style>
        .toolbar {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
        }

        h2 {
          color: white;
          font-size: 1.4rem;
          margin: 0;
          text-shadow: 0 2px 4px rgba(0,0,0,0.3);
        }

        .btn-primary {
          background: white;
          color: #764ba2;
          border: none;
          padding: 8px 16px;
          border-radius: 8px;
          font-weight: 600;
          cursor: pointer;
          font-size: 0.9rem;
        }

        .btn-primary:hover { opacity: 0.9; }

        table {
          width: 100%;
          border-collapse: collapse;
          background: white;
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
        }

        th {
          background: #f8f8f8;
          text-align: left;
          padding: 12px 16px;
          font-size: 0.78rem;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          color: #666;
          border-bottom: 1px solid #eee;
        }

        td {
          padding: 12px 16px;
          border-bottom: 1px solid #f0f0f0;
          font-size: 0.95rem;
        }

        tr:last-child td { border-bottom: none; }

        .role-badge {
          display: inline-block;
          padding: 2px 8px;
          border-radius: 12px;
          font-size: 0.78rem;
          font-weight: 600;
          background: #ede9fe;
          color: #5b21b6;
          margin-right: 4px;
        }

        .edit-btn {
          background: none;
          border: 1px solid #ddd;
          border-radius: 6px;
          padding: 4px 10px;
          cursor: pointer;
          color: #666;
          font-size: 0.85rem;
        }

        .edit-btn:hover { border-color: #764ba2; color: #764ba2; }

        .empty {
          text-align: center;
          padding: 40px;
          color: rgba(255,255,255,0.7);
        }

        .load-more {
          margin-top: 16px;
          text-align: center;
        }

        .btn-secondary {
          background: rgba(255,255,255,0.2);
          color: white;
          border: 1px solid rgba(255,255,255,0.4);
          padding: 8px 20px;
          border-radius: 8px;
          cursor: pointer;
          font-size: 0.9rem;
        }

        .btn-secondary:disabled { opacity: 0.6; cursor: default; }

        .loading {
          text-align: center;
          padding: 40px;
          color: rgba(255,255,255,0.7);
        }
      </style>

      <div class="toolbar">
        <h2>Users</h2>
        <button class="btn-primary" id="new-btn">+ New User</button>
      </div>
      <div id="content"><p class="loading">Loading…</p></div>
    `;

    this.root.getElementById('new-btn')!.addEventListener('click', () => {
      backboneService.navigateTo('/admin/users/new');
    });
  }

  renderContent() {
    const content = this.root.getElementById('content');
    if (!content) return;

    if (this._users.length === 0) {
      content.innerHTML = `<p class="empty">No users found.</p>`;
      return;
    }

    const rows = this._users.map(u => {
      const roles = (u.roles ?? []).map(r => `<span class="role-badge">${r}</span>`).join('');
      return `
        <tr>
          <td>${u.name ?? ''}</td>
          <td>${u.email ?? ''}</td>
          <td>${u.userType ?? '—'}</td>
          <td>${roles || '—'}</td>
          <td><button class="edit-btn" data-id="${u.userId ?? ''}">Edit</button></td>
        </tr>
      `;
    }).join('');

    content.innerHTML = `
      <table>
        <thead>
          <tr>
            <th>Name</th><th>Email</th><th>Type</th><th>Roles</th><th></th>
          </tr>
        </thead>
        <tbody>${rows}</tbody>
      </table>
      ${this._nextPageToken
        ? `<div class="load-more"><button class="btn-secondary" id="load-more-btn">Load more</button></div>`
        : ''}
    `;

    content.querySelectorAll<HTMLElement>('.edit-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        backboneService.navigateTo(`/admin/users/${btn.dataset.id}/edit`);
      });
    });

    content.querySelector('#load-more-btn')?.addEventListener('click', (e) => {
      const btn = e.currentTarget as HTMLButtonElement;
      btn.disabled = true;
      btn.textContent = 'Loading…';
      void this.loadUsers(this._nextPageToken);
    });
  }
}

customElements.define('hat-admin-users', HatAdminUsers);